// [NEW FILE] PM-8: "My activity", the employee's own audit trail.
// The organisation audit log (GET /audit-logs) is for administrators only. Every employee may read the history of
// their OWN appraisals (GET /records/{id}?include=history: goals submitted, approved, returned, assessments,
// results released, deadline changes ...), so this page lists those events for all of the employee's cycles.
// Sign-in events are not included: the backend shows them only in the administrators' audit trail.
import api from "./api";
import { getMe, listCyclesCached, listRecords, recordSection, nameOf, isoUtc, ok, pMapSettled, stripExtensionTag } from "./core";
import { BACKEND } from "../config/backendFeatures";

const EVENT_TEXT = {
  RECORD_CREATED: "Your appraisal was created",
  GOALS_SUBMITTED: "Goals submitted for approval",
  GOALS_APPROVED: "Goals approved",
  GOALS_CHANGES_REQUESTED: "Changes requested on your goals",
  ASSESSMENT_SUBMITTED: "Assessment submitted",
  RESULT_RELEASED: "Result released to you",
  RESULT_ACKNOWLEDGED: "Result acknowledged",
  REVIEWER_CHANGED: "Your reviewer was changed",
  DEADLINE_OVERRIDDEN: "A deadline was changed",
  GOAL_EDITED_BY_REVIEWER: "Your goal was edited by your reviewer",
  LOGIN: "You signed in",
  LOGIN_SUCCESS: "You signed in",
  SSO_IDENTITY_AUTO_LINKED: "Your Microsoft account was linked",
  SSO_IDENTITY_LINKED: "Your Microsoft account was linked",
  PASSWORD_RESET: "Your password was reset",
  PASSWORD_RESET_REQUESTED: "A password reset was requested",
  PASSWORD_RESET_COMPLETED: "Your password was reset",
  ACCOUNT_CREATED: "Your login was created",
  ACCOUNT_INVITATION_ACCEPTED: "You accepted the login invitation",
  ACCOUNT_STATUS_CHANGED: "Your login status was changed",
  LOGIN_FAILED: "A sign-in attempt failed",
  LOGOUT: "You signed out",
  PASSWORD_CHANGED: "Your password was changed",
};

const sentence = (code = "") => EVENT_TEXT[code] || code.toLowerCase().replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

// [NEW] PM-8 backend-ready: with REACT_APP_BACKEND_MY_AUDIT_LOG=true the user's own audit log (GET /audit-logs/me,
// any role, also sign-ins and settings changes) is added. If the endpoint is missing or fails, the page still works.
async function myAuditLog(me) {
  if (!BACKEND.myAuditLog) return [];
  try {
    const { data } = await api.get("/audit-logs/me", { params: { limit: 200 } });
    const rows = Array.isArray(data) ? data : data?.items || [];
    return rows.filter((e) => !String(e.event_type || "").startsWith("AUDIT_")).map((e, i) => ({
      key: `audit-${e.activity_id ?? e.audit_log_id ?? i}`,
      at: isoUtc(e.created_at || e.occurred_at),
      event: e.event_type || e.action,
      text: sentence(e.event_type || e.action || ""),
      by: !e.actor_employee_id || e.actor_employee_id === me.employee_id ? "You" : nameOf(e.actor_employee_id),
      reason: stripExtensionTag(e.reason || ""),
      cycleName: e.cycle_name || "",
    }));
  } catch {
    return [];
  }
}

export async function getMyActivity() {
  const [me, cycles] = await Promise.all([getMe(), listCyclesCached()]);
  const perCycle = await pMapSettled(cycles, async (cycle) => {
    const records = (await listRecords(cycle.id)).filter((r) => r.employee_id === me.employee_id);
    const lists = await Promise.all(records.map((r) => recordSection(r.performance_record_id, "history", [])));
    return lists.flat().map((e, i) => ({
      key: `${cycle.id}-${i}-${e.created_at}`,
      at: isoUtc(e.created_at),
      event: e.event_type,
      text: sentence(e.event_type),
      by: e.actor_employee_id === me.employee_id ? "You" : nameOf(e.actor_employee_id),
      reason: stripExtensionTag(e.reason || ""),
      cycleName: cycle.name,
    }));
  }, 3, []);
  const audit = await myAuditLog(me);
  const seen = new Set();
  const merged = [...perCycle.flat(), ...audit].filter((r) => {
    const id = `${r.event}|${String(r.at).slice(0, 19)}`; // the same event can come from both sources
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
  return ok(merged.sort((a, b) => String(b.at).localeCompare(String(a.at))));
}
